import { forwardRef } from "react";
import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import type { ButtonHTMLAttributes, ForwardedRef } from "react";

type Params = {
  color?: "default" | "warning";
  showIndicator?: boolean;
};

type BtnProps = Params & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color">;

const ButtonMiniBase = (
  props: BtnProps,
  ref: ForwardedRef<HTMLButtonElement>,
) => {
  const {
    children,
    color,
    type = "button",
    showIndicator,
    ...buttonProps
  } = props;

  return (
    <button
      ref={ref}
      type={type}
      css={btnStyle({ color, showIndicator })}
      {...buttonProps}
    >
      {children}
    </button>
  );
};

export const IconButtonMiniV2 = forwardRef<HTMLButtonElement, BtnProps>(
  ButtonMiniBase,
);

const btnStyle =
  ({ color = "default", showIndicator }: Params) =>
  ({ colorsV2 }: ExactTheme) => {
    const colorMap = {
      default: colorsV2.grey[800],
      warning: colorsV2.orange[500],
    };

    return css({
      all: "unset",
      position: "relative",
      display: "grid",
      placeContent: "center",
      cursor: "pointer",
      height: 16,
      width: 16,
      borderRadius: 4,
      color: colorMap[color],
      transition: "background-color 0.2s",

      ":hover": {
        backgroundColor: colorsV2.grey[100],
      },

      ":active": {
        backgroundColor: colorsV2.grey[200],
      },

      ...(showIndicator && {
        "::after": {
          content: '""',
          position: "absolute",
          top: -2,
          right: -2,
          width: 4,
          height: 4,
          borderRadius: "50%",
          backgroundColor: colorsV2.orange[500],
        },
      }),

      ":disabled": {
        opacity: 0.5,
        pointerEvents: "none",
      },
    });
  };
